import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/lib/supabase/client';
import { useToast } from './useToast';
import { Operation, OperationFilter, OperationFormData } from '@/types/operations';
import { UUID } from '@/types/common';

export function useOperations(filter: OperationFilter = { status: 'active' }) {
  const [operations, setOperations] = useState<Operation[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  // Generate a unique operation code (prefix + sequential number)
  const generateOperationCode = async (prefix: string = 'OP') => {
    try {
      // Get all operation codes that match the pattern: prefix-digits only (e.g., OP-001, OP-002)
      // This excludes codes like OP-WELD-001, OP-TURN-001, etc.
      const { data, error } = await supabase
        .from('operations')
        .select('operation_code')
        .not('operation_code', 'is', null);

      if (error) throw error;

      let maxNum = 0;

      if (data && data.length > 0) {
        // Filter for codes that match the pattern: prefix-digits (e.g., OP-001, OP-002)
        // and extract the highest number
        const pattern = new RegExp(`^${prefix}-(\\d+)$`);
        data.forEach(row => {
          if (row.operation_code) {
            const match = row.operation_code.match(pattern);
            if (match && match[1]) {
              const num = parseInt(match[1], 10);
              if (num > maxNum) {
                maxNum = num;
              }
            }
          }
        });
      }

      // Increment to get the next number
      const nextNum = maxNum + 1;

      // Format with leading zeros (e.g., OP-001)
      return `${prefix}-${nextNum.toString().padStart(3, '0')}`;
    } catch (err) {
      console.error('Error generating operation code:', err);
      // Fallback to timestamp-based code
      return `${prefix}-${Date.now().toString().slice(-6)}`;
    }
  };

  // Fetch operations with filter
  const fetchOperations = useCallback(async (currentFilter?: OperationFilter) => {
    const shouldShowLoading = operations.length === 0;
    if (shouldShowLoading) {
      setIsLoading(true);
    }
    setError(null);

    try {
      let query = supabase
        .from('operations')
        .select(`
          id,
          operation_code,
          name,
          description,
          department_id,
          default_workstation_id,
          setup_time,
          process_time,
          cost_per_hour,
          is_subcontracted,
          subcontractor_id,
          qc_required,
          status,
          created_by,
          created_at,
          updated_at,
          departments:department_id (
            name
          ),
          workstations:default_workstation_id (
            name
          ),
          suppliers:subcontractor_id (
            name
          )
        `);

      if (currentFilter) {
        if (currentFilter.status) {
          query = query.eq('status', currentFilter.status);
        }

        if (currentFilter.department_id) {
          query = query.eq('department_id', currentFilter.department_id);
        }

        if (currentFilter.is_subcontracted !== undefined) {
          query = query.eq('is_subcontracted', currentFilter.is_subcontracted);
        }

        if (currentFilter.qc_required !== undefined) {
          query = query.eq('qc_required', currentFilter.qc_required);
        }

        if (currentFilter.search) {
          query = query.or(`name.ilike.%${currentFilter.search}%,operation_code.ilike.%${currentFilter.search}%`);
        }
      }

      const { data, error: fetchError } = await query
        .order('name')
        .returns<any[]>();

      if (fetchError) throw fetchError;

      const transformedData = data.map(item => ({
        ...item,
        department_name: item.departments?.name,
        default_workstation_name: item.workstations?.name,
        subcontractor_name: item.suppliers?.name,
      }));

      setOperations(transformedData);
      return transformedData;
    } catch (err: any) {
      setError(err.message || 'An unknown error occurred.');
      console.error('Error fetching operations:', err);
      return undefined;
    } finally {
      setIsLoading(false);
    }
  }, []); // Empty dependency array, fetchOperations itself doesn't depend on state

  useEffect(() => {
    fetchOperations(filter);
  }, [JSON.stringify(filter), fetchOperations]);

  // Get a single operation by ID
  const getOperation = async (id: string) => {
    try {
      console.log("Fetching operation with ID:", id);
      const { data, error } = await supabase
        .from('operations')
        .select(`
          *,
          departments:department_id (
            name
          ),
          workstations:default_workstation_id (
            name
          ),
          suppliers:subcontractor_id (
            name
          )
        `)
        .eq('id', id)
        .single();

      if (error) {
        console.error("Supabase error:", error);
        throw error;
      }

      if (!data) {
        console.error("No data returned for operation:", id);
        return null;
      }

      // Transform the nested data
      const transformedData = {
        ...data,
        department_name: data.departments?.name,
        default_workstation_name: data.workstations?.name,
        subcontractor_name: data.suppliers?.name,
      };

      console.log("Operation data:", transformedData);
      return transformedData;
    } catch (err) {
      console.error(`Error fetching operation ${id}:`, err);
      toast({
        title: 'Error',
        description: 'Failed to load operation details',
        variant: 'destructive',
      });
      return null;
    }
  };

  // Create a new operation
  const createOperation = async (formData: OperationFormData, createdBy: string): Promise<string | null> => {
    try {
      // If no operation code is provided, generate one
      const operationCode = formData.operation_code || await generateOperationCode();

      // Sanitize UUID fields - convert empty strings to null
      const sanitizedData = {
        ...formData,
        operation_code: operationCode,
        created_by: createdBy,
        status: formData.status || 'active',
        department_id: formData.department_id && formData.department_id.trim() !== '' ? formData.department_id : null,
        default_workstation_id: formData.default_workstation_id && formData.default_workstation_id.trim() !== '' ? formData.default_workstation_id : null,
        subcontractor_id: formData.subcontractor_id && formData.subcontractor_id.trim() !== '' ? formData.subcontractor_id : null,
      };

      const { data, error } = await supabase
        .from('operations')
        .insert(sanitizedData)
        .select('id')
        .single();

      if (error) throw error;

      return data.id;
    } catch (err) {
      console.error('Error creating operation:', err);
      toast({
        title: 'Error',
        description: 'Failed to create operation',
        variant: 'destructive',
      });
      return null;
    }
  };

  // Update an existing operation
  const updateOperation = async (id: string, formData: OperationFormData): Promise<boolean> => {
    try {
      // Clean data to prevent empty string UUID issues
      const cleanedData = {...formData} as OperationFormData;
      
      // Handle department_id
      if (cleanedData.department_id === '' || cleanedData.department_id === 'none') {
        cleanedData.department_id = undefined;
      }
      
      // Handle default_workstation_id
      if (cleanedData.default_workstation_id === '' || cleanedData.default_workstation_id === 'none') {
        cleanedData.default_workstation_id = undefined;
      }
      
      // Handle subcontractor_id
      if (cleanedData.subcontractor_id === '' || cleanedData.subcontractor_id === 'none' || !cleanedData.is_subcontracted) {
        cleanedData.subcontractor_id = undefined;
      }
      
      console.log("Updating operation with cleaned data:", cleanedData);
      
      const { error } = await supabase
        .from('operations')
        .update({
          ...cleanedData,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (error) {
        console.error("Operation update error:", error);
        throw error;
      }

      return true;
    } catch (err) {
      console.error(`Error updating operation ${id}:`, err);
      toast({
        title: 'Error',
        description: 'Failed to update operation',
        variant: 'destructive',
      });
      return false;
    }
  };

  // Delete an operation
  const deleteOperation = async (id: string): Promise<boolean> => {
    try {
      // Check if operation is being used in routing_operations or work_order_operations
      const { data: routingOps, error: routingError } = await supabase
        .from('routing_operations')
        .select('id')
        .eq('operation_id', id);

      if (routingError) throw routingError;

      const { data: workOrderOps, error: workOrderError } = await supabase
        .from('work_order_operations')
        .select('id')
        .eq('operation_id', id);

      if (workOrderError) throw workOrderError;

      // If operation is being used, show error message
      if ((routingOps && routingOps.length > 0) || (workOrderOps && workOrderOps.length > 0)) {
        toast({
          title: 'Cannot Delete Operation',
          description: 'This operation is being used in routing or work orders. Please remove those references first.',
          variant: 'destructive',
        });
        return false;
      }

      // Delete related records first (cascade delete)
      // Delete operation_attachments
      const { error: attachmentsError } = await supabase
        .from('operation_attachments')
        .delete()
        .eq('operation_id', id);

      if (attachmentsError) throw attachmentsError;

      // Delete workstation_operations
      const { error: workstationOpsError } = await supabase
        .from('workstation_operations')
        .delete()
        .eq('operation_id', id);

      if (workstationOpsError) throw workstationOpsError;

      // Now delete the operation itself
      const { error } = await supabase
        .from('operations')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Update the local state
      setOperations(prevOperations => prevOperations.filter(op => op.id !== id));

      toast({
        title: 'Success',
        description: 'Operation deleted successfully',
      });

      return true;
    } catch (err) {
      console.error(`Error deleting operation ${id}:`, err);
      toast({
        title: 'Error',
        description: 'Failed to delete operation',
        variant: 'destructive',
      });
      return false;
    }
  };

  // Update operation status (activate/deactivate)
  const updateOperationStatus = async (id: UUID, status: 'active' | 'inactive'): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('operations')
        .update({ 
          status,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (error) throw error;

      // Update the local state
      setOperations(prevOperations => 
        prevOperations.map(op => 
          op.id === id ? { ...op, status } : op
        )
      );
      
      return true;
    } catch (err) {
      console.error(`Error updating operation status ${id}:`, err);
      toast({
        title: 'Error',
        description: 'Failed to update operation status',
        variant: 'destructive',
      });
      return false;
    }
  };

  return {
    operations,
    isLoading,
    error,
    fetchOperations,
    getOperation,
    createOperation,
    updateOperation,
    deleteOperation,
    updateOperationStatus,
    generateOperationCode,
  };
}
